import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { getRouterParamsId } from '@app/app.reducer';
import { getJobsSelected } from '@collections/jobs';
import { loadLicenseExtensions } from '@core/settings/license-configuration';
import { NotePageTypeEnum } from '@modules/notes/models/note-page-type.enum';
import { NoteSubject } from 'mojo-irp-notes';
import { getSelectedTalentPoolId } from 'mojo-talent-pools';

export const NOTES_FILTERS_PARAMS_DEFAULT_PAGE_SIZE = 10;
export const NOTES_FILTERS_PARAMS_DEFAULT_PAGE_NUMBER = 0;

export interface NotesFiltersInterface {
  id: string;
  isForRecruitingTeamTab: boolean;
  isForHiringTeamTab: boolean;
  systemGenerated: boolean;
  userGenerated: boolean;
  searchPhrases: string[];
  pageNumber: number;
  pageSize: number;
  anonymised?: boolean;
  subjectId?: number;
}

@Injectable()
export class NotesFiltersService {
  private isMultiTabsNotesFeatureExtensionEnabled$ = this.store
    .select(loadLicenseExtensions)
    .pipe(
      map(
        (licenseExtensions) => !licenseExtensions?.includes(AppFeatureExtensions.NOTES_SINGLE_TAB),
      ),
    );

  private currentRouteId$ = this.store.select(getRouterParamsId);

  private isOnJobPage$ = this.store.select(getJobsSelected).pipe(map(Boolean));
  private isOnTalentPoolPage$ = this.store.select(getSelectedTalentPoolId).pipe(map(Boolean));

  constructor(private store: Store) {}

  buildNotesFiltersId(
    subjectId: number,
    userId: number,
    isForHiringTeamTab: boolean,
    subject: string,
  ): Observable<string> {
    return combineLatest([this.isMultiTabsNotesFeatureExtensionEnabled$]).pipe(
      take(1),
      map(([isMultiTabsNotesFeatureExtensionEnabled]) => {
        let id;
        switch (subject) {
          case NoteSubject.Candidate:
            id = `notes-filters-${NotePageTypeEnum.CANDIDATE}:`;
            break;
          case NoteSubject.TalentPool:
            id = `notes-filters-${NotePageTypeEnum.TALENT_POOL}:`;
            break;
          default:
            id = `notes-filters-${NotePageTypeEnum.JOB}:`;
            break;
        }

        id = id.concat(subjectId?.toString(10));
        id = id.concat(':').concat(userId.toString(10));
        id = isMultiTabsNotesFeatureExtensionEnabled
          ? isForHiringTeamTab
            ? id.concat(':isForHiringTeamTab')
            : id.concat(':isForRecruitingTeamTab')
          : id.concat(':singleNotesTab');
        return id;
      }),
    );
  }

  buildNotesFilters(
    subjectId: number,
    userId: number,
    userGenerated: boolean,
    systemGenerated: boolean,
    isForHiringTeamTab: boolean,
    isForRecruitingTeamTab: boolean,
    subject: string,
  ): Observable<NotesFiltersInterface> {
    return combineLatest([
      this.buildNotesFiltersId(subjectId, userId, isForHiringTeamTab, subject),
      this.isOnJobPage$,
      this.currentRouteId$,
      this.isOnTalentPoolPage$,
    ]).pipe(
      take(1),
      map(([id]) => ({
        id,
        isForRecruitingTeamTab,
        isForHiringTeamTab,
        searchPhrases: [],
        pageNumber: NOTES_FILTERS_PARAMS_DEFAULT_PAGE_NUMBER,
        pageSize: NOTES_FILTERS_PARAMS_DEFAULT_PAGE_SIZE,
        userGenerated,
        systemGenerated,
        subjectId,
      })),
    );
  }
}
